import axios from 'axios';
import { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import Layout from '../../../../components/Layouts/Layouts/Layout';
import DashboardHeader from '../../../../components/basic/DashboardHeader';
import { formatDateAndTime } from '../../../../components/basic/TimeAndDate';
import { getURLbyEndPointV2 } from '../../../../store/api';

const ParticipateCountry = () => {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [error, setError] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState({
        country: '',
        companyName: '',
        heading: '',
        description: '',
        logo: null,
    });
    const [formErrors, setFormErrors] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchCountries();
    }, []);

    const fetchCountries = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axios.get(getURLbyEndPointV2('getParticipateCountries'));

            const formattedData = response.data.data.flatMap((country) =>
                country.details.map((detail) => ({
                    _id: detail._id,
                    country: country.country,
                    companyName: detail.companyName || 'N/A',
                    heading: detail.heading || 'N/A',
                    description: detail.description || 'N/A',
                    logoUrl: detail.logoUrl || country.logoUrl || null,
                    createdAt: detail.createdAt,
                }))
            );

            setCountries(formattedData);
        } catch (error) {
            console.error('Error fetching countries:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddClick = () => {
        setIsEditMode(false);
        setSelectedCountry(null);
        setFormData({
            country: '',
            companyName: '',
            heading: '',
            description: '',
            logo: null,
        });
        setFormErrors({});
        setShowModal(true);
    };

    const handleEditClick = (company) => {
        setIsEditMode(true);
        setSelectedCountry(company);
        setFormData({
            companyName: company.companyName,
            country: company.country,
            heading: company.heading,
            description: company.description,
            logo: null,
        });
        setFormErrors({});
        setShowModal(true);
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.country && !isEditMode) {
            errors.country = 'Country is required';
        }
        if (!formData.companyName && !isEditMode) {
            errors.companyName = 'Company name is required';
        }
        if (!formData.heading) {
            errors.heading = 'Heading is required';
        }
        if (!formData.description) {
            errors.description = 'Description is required';
        }
        if (formData.description && formData.description.length > 5000) {
            errors.description = 'Description must be less than 5000 characters';
        }
        if (!formData.logo && !isEditMode) {
            errors.logo = 'Logo is required';
        }
        const maxSize = 5 * 1024 * 1024;
        if (formData.logo && formData.logo.size > maxSize) {
            errors.logo = 'Logo file size must be less than 5MB';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (files && files[0]) {
            const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
            if (!validTypes.includes(files[0].type)) {
                setFormErrors(prev => ({
                    ...prev,
                    [name]: 'Please upload only image files (JPEG, PNG, GIF)'
                }));
                e.target.value = '';
                return;
            }
            setFormData(prev => ({ ...prev, [name]: files[0] }));
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSubmitLoading(true);
            setError('');

            const formDataToSend = new FormData();
            formDataToSend.append('heading', formData.heading);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('companyName', formData.companyName);
            if (formData.logo) {
                formDataToSend.append('logo', formData.logo);
            }

            if (!isEditMode) {
                formDataToSend.append('country', formData.country);
                await axios.post(
                    getURLbyEndPointV2('createParticipateCountry'),
                    formDataToSend,
                    {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    }
                );
            } else {
                await axios.put(
                    getURLbyEndPointV2('updateParticipateCountry') + selectedCountry._id,
                    formDataToSend,
                    {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    }
                );
            }

            setShowModal(false);
            fetchCountries();
        } catch (error) {
            setError(`Failed to ${isEditMode ? 'update' : 'create'} participate country. Please try again.`);
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} participate country:`, error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const filteredCountries = countries.filter(country =>
        country.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.companyName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Layout>
            <div className='d-flex flex-column justify-content-center align-items-center px-3'>
                <div className='main-width d-flex flex-column'>
                    <hr />
                    <DashboardHeader heading='EVENTS / B2B PARTICIPATE COUNTRY' backUrl='/b2b-virtual-forum' />
                    <hr />

                    <Modal size='xl' show={showModal} onHide={() => setShowModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{isEditMode ? 'Edit' : 'Add'} Participate Country</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {error && <div className="alert alert-danger">{error}</div>}
                            <Form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Company Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="companyName"
                                                value={formData.companyName}
                                                onChange={handleInputChange}
                                                disabled={isEditMode}
                                                isInvalid={!!formErrors.companyName}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {formErrors.companyName}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </div>

                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Country</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleInputChange}
                                                disabled={isEditMode}
                                                isInvalid={!!formErrors.country}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {formErrors.country}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </div>
                                </div>

                                <Form.Group className="mb-3">
                                    <Form.Label>Heading</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="heading"
                                        value={formData.heading}
                                        onChange={handleInputChange}
                                        isInvalid={!!formErrors.heading}
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.heading}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        isInvalid={!!formErrors.description}
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.description}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Logo</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="logo"
                                        onChange={handleFileChange}
                                        accept="image/*"
                                        isInvalid={!!formErrors.logo}
                                        required={!isEditMode}
                                    />
                                    <Form.Text className="text-muted">
                                        Max size: 5MB. Accepted formats: JPEG, PNG, GIF
                                    </Form.Text>
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.logo}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <div className="text-center">
                                    <Button type="submit" disabled={submitLoading}>
                                        {submitLoading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                {isEditMode ? 'Updating...' : 'Creating...'}
                                            </>
                                        ) : (
                                            isEditMode ? 'Update' : 'Create'
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal>

                    <div className='d-flex justify-content-between mb-3'>
                        <Button variant="primary" onClick={handleAddClick}>
                            Add Participate Country
                        </Button>
                        <input
                            className='form-control w-25'
                            type='search'
                            placeholder='Search by company name or country'
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <Table responsive striped bordered hover>
                        <thead>
                            <tr>
                                <th>Sr No.</th>
                                <th>Country</th>
                                <th>Company Name</th>
                                <th>Heading</th>
                                <th>Description</th>
                                <th>Logo</th>
                                <th>Created At</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="text-center">
                                        <Spinner animation="border" />
                                    </td>
                                </tr>
                            ) : filteredCountries.length > 0 ? (
                                filteredCountries.map((company, index) => (
                                    <tr key={company._id}>
                                        <td>{index + 1}</td>
                                        <td>{company.country}</td>
                                        <td>{company.companyName}</td>
                                        <td>{company.heading}</td>
                                        <td>{company.description}</td>
                                        <td>
                                            {company.logoUrl && (
                                                <img
                                                    src={company.logoUrl}
                                                    alt="Logo"
                                                    style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                                                />
                                            )}
                                        </td>
                                        <td>{formatDateAndTime(company.createdAt)}</td>
                                        <td>
                                            <Button
                                                size="sm"
                                                variant="warning"
                                                onClick={() => handleEditClick(company)}
                                            >
                                                Edit
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={8} className="text-center">
                                        No Matching Data
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </Layout>
    );
};

export default ParticipateCountry;